import {
  createContext,
  useContext,
  useEffect,
  useState,
  type ReactNode,
} from "react";
import { auth, isFirebaseConfigured, firebaseReady } from "./firebase";

interface AuthContextValue {
  user: any;
  loading: boolean;
  token: string | null;
  isDemo: boolean;
  loginWithEmail: (email: string, password: string) => Promise<void>;
  loginWithGoogle: () => Promise<void>;
  logout: () => Promise<void>;
}

const AuthContext = createContext<AuthContextValue | null>(null);

const DEMO_USER = {
  uid: "demo-user",
  email: "demo@familytree.app",
  displayName: "Demo User",
};

export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<any>(isFirebaseConfigured ? null : DEMO_USER);
  const [token, setToken] = useState<string | null>(isFirebaseConfigured ? null : "demo-token");
  const [loading, setLoading] = useState(isFirebaseConfigured);
  const isDemo = !isFirebaseConfigured;

  useEffect(() => {
    if (!isFirebaseConfigured) return;

    let unsubscribe: (() => void) | undefined;
    firebaseReady.then(async () => {
      if (!auth) return;
      const { onAuthStateChanged } = await import("firebase/auth");
      unsubscribe = onAuthStateChanged(auth, async (firebaseUser: any) => {
        setUser(firebaseUser);
        if (firebaseUser) {
          const idToken = await firebaseUser.getIdToken();
          setToken(idToken);
        } else {
          setToken(null);
        }
        setLoading(false);
      });
    });
    return () => unsubscribe?.();
  }, []);

  const loginWithEmail = async (email: string, password: string) => {
    if (!auth) return;
    const { signInWithEmailAndPassword } = await import("firebase/auth");
    await signInWithEmailAndPassword(auth, email, password);
  };

  const loginWithGoogle = async () => {
    if (!auth) return;
    const { signInWithPopup, GoogleAuthProvider } = await import("firebase/auth");
    await signInWithPopup(auth, new GoogleAuthProvider());
  };

  const logout = async () => {
    if (!auth) return;
    const { signOut } = await import("firebase/auth");
    await signOut(auth);
  };

  return (
    <AuthContext.Provider
      value={{ user, loading, token, isDemo, loginWithEmail, loginWithGoogle, logout }}
    >
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error("useAuth must be used within an AuthProvider");
  }
  return context;
}
